import { LightningQueryParams, LightningResponse } from '../types/lightning';
import { logger } from '../utils/logger';
import { getBackendUrl } from './configService';

const log = logger('LightningService');

export class LightningService {
  private getBaseUrl(): string {
    const backendUrl = getBackendUrl();
    if (!backendUrl) {
      throw new Error('FATAL: BACKEND_URL not configured in runtime config');
    }
    return `${backendUrl}/api/lightning`;
  }

  async fetchData(params: LightningQueryParams = {}): Promise<LightningResponse> {
    try {
      log.debug('LightningService: Fetching strikes', params);

      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.chargeType) queryParams.append('chargeType', params.chargeType);
      if (params.cloudIndicator != null) queryParams.append('cloudIndicator', String(params.cloudIndicator));
      if (params.strikeType) queryParams.append('strikeType', params.strikeType);

      const numKeys: (keyof LightningQueryParams)[] = [
        'minCurrent', 'maxCurrent', 'minLat', 'maxLat', 'minLon', 'maxLon',
        'minMultiplicity', 'maxMultiplicity', 'minSensors', 'maxSensors',
        'minAngle', 'maxAngle', 'minSignal', 'maxSignal',
      ];
      for (const key of numKeys) {
        if (params[key] != null) queryParams.append(key, String(params[key]));
      }

      // Strict inequality flags
      const strictKeys: (keyof LightningQueryParams)[] = [
        'minCurrentStrict', 'maxCurrentStrict', 'minLatStrict', 'maxLatStrict',
        'minLonStrict', 'maxLonStrict', 'minMultiplicityStrict', 'maxMultiplicityStrict',
        'minSensorsStrict', 'maxSensorsStrict', 'minAngleStrict', 'maxAngleStrict',
        'minSignalStrict', 'maxSignalStrict',
      ];
      for (const key of strictKeys) {
        if (params[key]) queryParams.append(key, String(params[key]));
      }

      const url = `${this.getBaseUrl()}?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      log.debug(`LightningService: Received ${data.data?.length || 0} strikes`);

      return data;
    } catch (error) {
      log.error('LightningService: Fetch error', error);
      throw error;
    }
  }
}

export const lightningService = new LightningService();
export default lightningService;
